package com.evoapps.evoshift.domain.usecase.availability

import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.RosterStatus
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.usecase.CreateRosterUseCase
import com.evoapps.evoshift.domain.model.AvailabilityEmailLinks
import com.evoapps.evoshift.domain.usecase.SendAvailabilityRequestUseCase
import com.evoapps.evoshift.domain.util.AppClock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number

/** SH-172: availability is asked for the current month or a later one, never a past one. */
class AvailabilityMonthPassedException(val year: Int, val month: Int) : Exception("$year-$month has passed")

/** SH-172: the month is published (or locked): its availability is no longer asked for. */
class AvailabilityMonthClosedException(val rosterId: String) : Exception("$rosterId is already published")

/**
 * SH-172: asks the team for availability for any month from the current one on, at any stage of
 * its roster. A month without a roster gets one first, empty (the roster document only, no
 * shifts, still [RosterStatus.SKELETON], id `workplaceId_YYYY_MM`; its cost snapshot and daily
 * records as for any new month), so the request has a roster to be linked to and the demand can
 * be built beside the answers, or after them. Returns the roster's id.
 */
class RequestAvailabilityForMonthUseCase(
    private val rosterRepository: RosterRepository,
    private val createRoster: CreateRosterUseCase,
    private val sendRequest: SendAvailabilityRequestUseCase,
    private val today: () -> LocalDate = { AppClock.today() }
) {
    suspend operator fun invoke(
        managerId: String,
        workplaceId: String,
        year: Int,
        month: Int,
        startDate: String,
        endDate: String,
        dueDate: String,
        message: String?,
        // SH-173: also by e-mail (personal links), as for a roster's own request.
        emailLinks: AvailabilityEmailLinks = AvailabilityEmailLinks.NONE
    ): Result<String> = runCatching {
        require(month in 1..12) { "Not a month: $month" }
        val now = today()
        if (year * 100 + month < now.year * 100 + now.month.number) throw AvailabilityMonthPassedException(year, month)
        val rosterId = RosterIds.of(workplaceId, year, month)
        val existing: Roster? = rosterRepository.getRoster(rosterId).getOrNull()
        when {
            existing == null -> createRoster(workplaceId, month, year, withSkeleton = false).getOrThrow()
            existing.status != RosterStatus.SKELETON && existing.status != RosterStatus.DRAFT -> throw AvailabilityMonthClosedException(rosterId)
        }
        val monthIso = "$year-${month.toString().padStart(2, '0')}"
        sendRequest(managerId, workplaceId, rosterId, monthIso, startDate, endDate, dueDate, message, emailLinks).getOrThrow()
        rosterId
    }
}
